import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Button,
  Grid,
  TextField,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { get, isBoolean } from "lodash";
import PropTypes from "prop-types";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import { SUPER_ADMIN } from "common/constant";
import InputFieldText from "Components/Common/InputFieldText";
import Tooltip from "Components/Common/Tooltip";
import {
  getOptionLabel,
  getOptionValue,
  otherUtilitySubscriptionOptions,
} from "common/enums";

import ConnectionTypeReadingModel from "./ConnectionTypeReadingModel";
import BillMeterReadingForm from "./BillMeterReadingForm";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import getFileExtension from "utils/getFileExtension";
import { filterEmptyStrings } from "utils";

import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import {
  useLazyGetOtherUtilityMeterReadingQuery,
  usePostOtherUtilityUploadMeterReadingsFileMutation,
} from "apis/MeterReading";

const initialQuery = {
  meterNumber: "",
  consumerNumber: "",
};
const validFileFormats = ["xls", "xlsx"];
const billCycleStatusToDisableEdit = ["closed"];

const getMeterDetailsValue = (data = [], key) =>
  data.map((obj) => get(obj, key) ?? "-").join(" / ");

const getDemandChargesValue = (data = [], key) =>
  data
    .map((obj) =>
      get(obj, key) ? "Yes" : isBoolean(get(obj, key)) ? "No" : "--"
    )
    .join(" / ");

const OtherUtilityBillMeterReading = ({ t }) => {
  const { utilityRoles } = useSelector((state) => state.Login.user);
  const { roles } = useSelector((state) => state.Login);

  const { data: { data: billingCycleOptions } = {} } =
    useGetBillingCycleOptionsQuery();

  const [uploadMeterReadingsFile, { isLoading: isMeterReadingFileUploading }] =
    usePostOtherUtilityUploadMeterReadingsFileMutation();

  // const [downloadExcel, { isLoading: isExcelFileDownloading }] =
  //   useLazyDownloadExcelQuery();

  const sm = useMediaQuery("(max-width: 599px)");

  const [
    getMeterReadings,
    {
      data: {
        data: {
          otherUtilityConsumerMeterReadings: meterReadings,
          billingCycleStatus,
        } = {},
      } = {},
      isLoading: isMeterReadingLoading,
      isFetching: isMeterReadingFetching,
    },
  ] = useLazyGetOtherUtilityMeterReadingQuery();

  const [utilityConsumerId, setUtilityConsumerId] = useState("");
  const [billCycleId, setBillCycleId] = useState("");
  const [query, setQuery] = useState(initialQuery);
  const [subscriptionType, setSubscriptionType] = useState("");
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [isConnectionTypeModalVisible, setIsConnectionTypeModalVisible] =
    useState(false);

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType: currentSubscriptionType }) =>
                currentSubscriptionType === subscriptionType
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions, subscriptionType]
  );

  const otherUtilityMeterSubscriptionOptions = useMemo(
    () =>
      roles.includes(SUPER_ADMIN)
        ? otherUtilitySubscriptionOptions
        : otherUtilitySubscriptionOptions.filter(({ utilityId }) =>
            utilityRoles.some(
              ({ utilityId: currentUtilityId }) =>
                currentUtilityId === utilityId
            )
          ),
    [otherUtilitySubscriptionOptions]
  );

  useEffect(() => {
    if (otherUtilityMeterSubscriptionOptions.length) {
      setSubscriptionType(otherUtilityMeterSubscriptionOptions[0].value);
    }
  }, [otherUtilityMeterSubscriptionOptions]);

  useEffect(() => {
    if (updatedBillingCycleOptions.length && subscriptionType) {
      const billingCycleId =
        updatedBillingCycleOptions.find(
          ({ status }) => status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "";
      setBillCycleId(billingCycleId);
      setQuery(initialQuery);
      getMeterReadingsData(billingCycleId, {
        ...initialQuery,
        subscriptionType,
      });
    }
  }, [updatedBillingCycleOptions]);

  const isMeterReadingNonEditable = useMemo(
    () =>
      billingCycleStatus
        ? billCycleStatusToDisableEdit.includes(
            getOptionLabel("billingStatus", billingCycleStatus).toLowerCase()
          )
        : false,
    [billingCycleStatus]
  );
  const isConnectionTypeVisible = useMemo(
    () =>
      subscriptionType ===
      getOptionValue("otherUtilityMeterSubscriptionOptions", "DCS"),
    [subscriptionType]
  );

  const columns = useMemo(
    () => [
      {
        title: "Consumer Number",
        field: "consumerNumber",
      },
      {
        title: "Meter Number",
        renderCell: ({ otherUtilityMeterReadings }) =>
          getMeterDetailsValue(otherUtilityMeterReadings, "meterNumber"),
      },
      {
        title: `${
          subscriptionType ===
          getOptionValue("otherUtilitySubscriptionOptions", "Solid Waste")
            ? "Buildup Area"
            : "Current Reading"
        }`,
        renderCell: ({ otherUtilityMeterReadings }) =>
          getMeterDetailsValue(otherUtilityMeterReadings, "currentReading"),
      },
      {
        title: "Adjustments",
        renderCell: ({ otherUtilityMeterReadings }) =>
          getMeterDetailsValue(otherUtilityMeterReadings, "adjustments"),
      },
      {
        title: "Meter Reading Date",
        renderCell: ({ otherUtilityMeterReadings }) =>
          otherUtilityMeterReadings
            ?.map(({ meterReadingDate }) =>
              convertUTCDateToLocalDate(meterReadingDate).format("DD-MM-YYYY")
            )
            .join(" / "),
      },
    ],
    [subscriptionType]
  );

  const tableColumns = useMemo(
    () => [
      ...(isConnectionTypeVisible
        ? [
            ...columns,
            {
              title: "Pro Rata Consumption",
              renderCell: ({ otherUtilityMeterReadings }) =>
                getMeterDetailsValue(
                  otherUtilityMeterReadings,
                  "proRataConsumption"
                ),
            },
            {
              title: "Actual Max Demand",
              renderCell: ({ otherUtilityMeterReadings }) =>
                getMeterDetailsValue(
                  otherUtilityMeterReadings,
                  "actualMaxDemand"
                ),
            },
            {
              title: "Return Water Temperature Penalty Charges",
              renderCell: ({ otherUtilityMeterReadings }) =>
                getMeterDetailsValue(
                  otherUtilityMeterReadings,
                  "returnWaterTemperaturePenaltyCharges"
                ),
            },
            {
              title: "Demand Charges",
              renderCell: ({ otherUtilityMeterReadings }) =>
                getDemandChargesValue(
                  otherUtilityMeterReadings,
                  "isDemandCharge"
                ),
            },
            {
              title: "Notes",
              renderCell: ({ otherUtilityMeterReadings }) =>
                getMeterDetailsValue(otherUtilityMeterReadings, "note"),
            },
          ]
        : columns),
      {
        title: "Actions",
        renderCell: ({ utilityConsumerId }) => (
          <IconButton
            onClick={() => setUtilityConsumerId(utilityConsumerId)}
            disabled={isMeterReadingNonEditable}
          >
            <Tooltip title={t("Edit Meter Readings")}>
              <EditIcon
                fontSize="small"
                color={isMeterReadingNonEditable ? "disabled" : "primary"}
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
        ),
      },
    ],
    [isConnectionTypeVisible, isMeterReadingNonEditable]
  );

  const handleUploadFile = async (selectedFile, connectionType = "") => {
    try {
      const formData = new FormData();
      const fileExtension = getFileExtension(selectedFile.name)?.toLowerCase();
      if (!validFileFormats.includes(fileExtension)) {
        toast.error(
          "Invalid file format, Please upload file with .xsl or .xlsx extension."
        );
      } else {
        formData.append("file", selectedFile);

        await uploadMeterReadingsFile(
          formData,
          filterEmptyStrings({
            tariff: connectionType,
            subscriptionType,
          })
        );

        setBillCycleId(updatedBillingCycleOptions[0]?.value ?? 0);
      }
    } catch (err) {}
  };

  // const downloadBtnHandler = async () => {
  //   try {
  //     if (billCycleId) {
  //       await downloadExcel({
  //         endpoint: `/report/billing-cycle/${billCycleId}/meter-reading-excel`,
  //         sheetName: "Meter Readings",
  //         billCycleId,
  //       });
  //     }
  //   } catch (error) {}
  // };

  const getMeterReadingsData = async (id, query) => {
    try {
      await getMeterReadings(id, query);
    } catch (error) {}
  };

  return (
    <div className="pb-2">
      {isConnectionTypeModalVisible && (
        <ConnectionTypeReadingModel
          handleClose={() => setIsConnectionTypeModalVisible(false)}
          handleUploadFile={handleUploadFile}
        />
      )}
      <Backdrop
        open={
          isMeterReadingLoading ||
          isMeterReadingFetching ||
          isMeterReadingFileUploading
          // || isExcelFileDownloading
        }
      />
      {!!utilityConsumerId && !!billCycleId && (
        <BillMeterReadingForm
          utilityConsumerId={utilityConsumerId}
          handleClose={() => setUtilityConsumerId("")}
          billCycleId={billCycleId}
          subscriptionType={subscriptionType}
        />
      )}
      <BreadCrumb title={t("Meter Readings")} pageTitle={t("Billing")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          getMeterReadingsData(billCycleId, {
            ...query,
            subscriptionType,
          });
          handleResetPage();
        }}
      >
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Subscription Type") + "*"}
              onChange={(_, { value }) => setSubscriptionType(value)}
              options={otherUtilityMeterSubscriptionOptions}
              value={
                otherUtilityMeterSubscriptionOptions.find(
                  ({ value }) => value === subscriptionType
                ) ?? ""
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              onChange={(_, { value }) => setBillCycleId(value)}
              value={
                updatedBillingCycleOptions.find(
                  (status) => status.value === billCycleId
                ) ?? ""
              }
              renderInput={(params) => {
                delete params.inputProps.id;
                delete params.id;
                return <TextField {...params} label={t("Select bill cycle")} />;
              }}
              options={updatedBillingCycleOptions}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              type="number"
              onChange={(e) => {
                setQuery({ ...query, consumerNumber: e.target.value });
              }}
              value={query.consumerNumber}
              placeholder={t("Enter consumer number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Meter Number")}
              onChange={(e) => {
                setQuery({ ...query, meterNumber: e.target.value });
              }}
              value={query.meterNumber}
              placeholder={t("Enter meter number")}
            />
          </Grid>

          <Grid item lg={8}>
            <Button
              variant="outlined"
              disabled={!updatedBillingCycleOptions.length}
              className="mb-2 me-2"
              type="submit"
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              disabled={!updatedBillingCycleOptions.length}
              onClick={() => {
                getMeterReadingsData(billCycleId, {
                  ...initialQuery,
                  subscriptionType,
                });
                setQuery(initialQuery);
                handleResetPage();
              }}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
            <Button
              variant="contained"
              disabled={isMeterReadingNonEditable}
              component="label"
              className="ms-1 mb-2 me-2"
              onClick={() => {
                if (isConnectionTypeVisible) {
                  setIsConnectionTypeModalVisible(true);
                }
              }}
            >
              {t("Upload Readings")}
              {!isConnectionTypeVisible && (
                <input
                  style={{
                    display: "none",
                  }}
                  accept=".xls, .xlsx"
                  type="file"
                  onChange={async (e) => {
                    await handleUploadFile(e.target.files[0]);
                    e.target.value = null;
                  }}
                />
              )}
            </Button>
            {/* 
          {!!meterReadings?.length && (
            <Button
              variant="contained"
              color="primary"
              className="ms-1 mb-2 me-2"
              onClick={downloadBtnHandler}
              disabled={!billCycleId}
            >
              {t("Download As xlsx")}
            </Button>
          )} */}
          </Grid>
        </Grid>
      </form>

      <BaseTable
        data={meterReadings || []}
        columns={tableColumns}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        displaySrNo
        pageSize={10}
        maxHeight={sm ? "calc(100vh - 330px)" : "calc(100vh - 300px)"}
      />
    </div>
  );
};

OtherUtilityBillMeterReading.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(OtherUtilityBillMeterReading);
